import {
  FormLabel,
  InputAdornment,
  InputProps,
  Input as MuiInput,
  SxProps,
  Typography,
} from "@mui/material";
import {
  ReactNode,
  useContext,
  ChangeEvent,
  useRef,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { FormContext } from "../../../context";
import { getErrorMessage } from "../../../utils/FormValidationsUtils";
import { Box } from "../Box";
import { Label } from "../Typography";
import styles from "./Input.module.scss";
import { AdminIcon, Icon } from "../Icon";
import { palette } from "../../../styles/theme";
import { PropsWithDataTest } from "../../../types";
import { useAuth } from "../../../common/hooks/useAuth";

interface Props
  extends PropsWithDataTest<Omit<InputProps, "error" | "onChange" | "size">> {
  label?: string;
  size?: InputProps["size"] | "extraSmall";
  subLabel?: string | ReactNode;
  link?: ReactNode;
  error?: string;
  name?: InputProps["name"];
  containerSx?: SxProps;
  onChange?: (event: ChangeEvent<HTMLInputElement>, value?: string) => void;
  labelSx?: SxProps;
  noMaxWidth?: boolean;
  noDefaultValue?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  adminOnly?: boolean;
  unit?: string;
  unitPosition?: "start" | "end";
  showPasswordToggle?: boolean;
}

export const Input = ({
  label,
  subLabel,
  link,
  name = "",
  labelSx,
  containerSx,
  noMaxWidth,
  noDefaultValue,
  clearable,
  onClear,
  "data-test": dataTest,
  adminOnly,
  unit,
  unitPosition = "start",
  showPasswordToggle,
  size,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { formik } = useContext(FormContext);
  const { isAdmin } = useAuth();
  const hasError = !!props.error || !!formik?.errors[name];
  const isTouched = formik?.touched[name];
  const ref = useRef<HTMLInputElement>(null);
  const [showPasswordAsText, setShowPasswordAsText] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    setShowPasswordAsText((prev) => !prev);
  }, []);

  if (adminOnly && !isAdmin) return null;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) {
      event.preventDefault();
      return;
    }
    formik
      ? formik?.setFieldValue(name, event.target.value)
      : props.onChange?.(event);
  };

  const getInputType = () => {
    if (showPasswordToggle && props.type === "password") {
      return showPasswordAsText ? "text" : "password";
    }
    return props.type;
  };

  const renderEndAdornment = () => {
    if (props.endAdornment) {
      return props.endAdornment;
    }

    if (showPasswordToggle && props.type === "password") {
      return (
        <InputAdornment position="end">
          <Icon
            style={{
              cursor: "pointer",
            }}
            icon={showPasswordAsText ? "visibility" : "visibility_off"}
            onClick={togglePasswordVisibility}
            size={20}
            color={palette.black.headings}
          />
        </InputAdornment>
      );
    }

    if (clearable && !!ref.current && !!ref.current.value) {
      return (
        <InputAdornment position="end">
          <Icon
            onClick={() => {
              handleChange({
                target: { value: "" },
              } as ChangeEvent<HTMLInputElement>);
              onClear?.();
            }}
            icon={"clear"}
            size={24}
            weight={300}
            color={palette.grey.nearBlack}
          />
        </InputAdornment>
      );
    }

    if (unit && unitPosition === "end") {
      return <InputAdornment position="end">{unit}</InputAdornment>;
    }

    return undefined;
  };

  return (
    <Box
      width="100%"
      maxWidth={noMaxWidth ? undefined : "400px"}
      display="flex"
      justifyItems="center"
      flexDirection="column"
      minWidth={props.fullWidth ? "100%" : undefined}
      sx={containerSx}
    >
      {link ? (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          {!!label && <Label htmlFor={props.id}>{label}</Label>}
          {link}
        </Box>
      ) : (
        !!label && (
          <Box display={"flex"} gap={"4px"} alignItems={"center"} mb={0.5}>
            <Label
              htmlFor={props.id}
              sx={{
                mb: subLabel ? "2px" : "0",
                textAlign: "start",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                ...labelSx,
              }}
            >
              {label}
              {!!subLabel && (
                <Typography variant="textXs" color={palette.grey.primary}>
                  {subLabel}
                </Typography>
              )}
            </Label>
            {adminOnly && <AdminIcon />}
          </Box>
        )
      )}

      <MuiInput
        onChange={handleChange}
        defaultValue={
          !props.value && !noDefaultValue
            ? formik?.initialValues[name]
            : undefined
        }
        {...props}
        type={getInputType()}
        data-test={dataTest}
        name={name as string}
        classes={{ root: styles.container }}
        error={!!hasError && !!isTouched}
        size={size as InputProps["size"]}
        sx={{
          ...props.sx,
          ...(unit &&
            unitPosition === "start" && {
              py: "0",
              pl: "0",
            }),
          ...(unit &&
            unitPosition === "end" && {
              borderRadius: "8px 0px 0px 8px",
            }),
          minWidth: props.fullWidth ? "100%" : undefined,
          ...(size === "extraSmall" && {
            fontSize: "12px",
            lineHeight: "15px",
          }),
        }}
        inputProps={{ ...props.inputProps, ref: ref }}
        startAdornment={
          unit && unitPosition === "start" ? (
            <InputAdornment
              position="start"
              sx={{
                backgroundColor: palette.grey.lightGrey,
                height: "100%",
                maxHeight: "none",
                p: "8px 12px",
                borderRight: `1px solid ${palette.grey.darkGrey}`,
                mr: "12px",
                ml: "0px",
                borderRadius: "8px 0px 0px 8px",
              }}
            >
              {unit}
            </InputAdornment>
          ) : (
            props.startAdornment
          )
        }
        endAdornment={renderEndAdornment()}
      />
      {!!formik?.errors[name] && !!isTouched && (
        <FormLabel sx={{ textAlign: "start" }} color="error" error>
          {getErrorMessage(t, formik?.errors[name])}
        </FormLabel>
      )}
      {!!props.error && (
        <FormLabel sx={{ textAlign: "start" }} color="error" error>
          {getErrorMessage(t, props.error)}
        </FormLabel>
      )}
    </Box>
  );
};
